import * as yup from "yup"
import { PRICING_TYPES, TICKET_TYPES, FEE_TYPES } from "../constants/events"

// Rounds to the nearest cent instead of rejecting values with more than two
// decimal places, matching the rounding convention already used for money in
// calculateBandShareForTicketPrice/calculateGrossPriceForBandShare (see
// util/ticketUtils.js).
function roundToTwoDecimals(value: number): number {
  return Math.round(value * 100) / 100
}

// Individual field validation schemas
export const bandNadaTicketPriceSchema = yup
  .number()
  .transform((value) => (isNaN(value) ? null : value))
  .transform((value) => (value === null ? null : roundToTwoDecimals(value)))
  .nullable()
  .typeError("Amount must be a number")
  .min(0, "Amount must be zero or more")

export const bandNadaTicketMaxSchema = yup
  .number()
  .transform((value) => (isNaN(value) ? null : value))
  .nullable()
  .positive("Must be a positive number")
  .integer("Must be a whole number")

export const ticketPriceAtDoorSchema = yup
  .number()
  .transform((value) => (isNaN(value) ? null : value))
  .transform((value) => (value === null ? null : roundToTwoDecimals(value)))
  .nullable()
  .typeError("Amount must be a number")
  .min(0, "Amount must be zero or more")

// Main validation schema
export const eventValidationSchema = yup.object().shape({
  title: yup
    .string()
    .required("Event name is required")
    .min(3, "Event name must be at least 3 characters"),
  eventDescription: yup
    .string()
    .required("Description is required")
    .min(10, "Description must be at least 10 characters"),
  startDate: yup.date().required("Start date is required"),
  coverImageUrl: yup.string().nullable(),
  durationMinutes: yup.number().required("Duration is required"),
  endsAt: yup.date().nullable(),
  eventStatus: yup.string().default("published"),
  eventLocation: yup
    .object()
    .transform((value) => (!value ? { displayName: "" } : value)),
  ticketType: yup
    .string()
    .required("Event type is required")
    .oneOf(
      [
        TICKET_TYPES.NO_ADVANCE_TICKETS.value,
        TICKET_TYPES.BN_TICKETS.value,
        TICKET_TYPES.EXTERNAL_TICKETS.value,
      ],
      "Invalid event type",
    ),
  bandNadaTicketPrice: yup.mixed().when(["ticketType", "pricingType"], {
    is: (ticketType: string, pricingType: string) =>
      ticketType === TICKET_TYPES.BN_TICKETS.value &&
      pricingType !== PRICING_TYPES.FREE.value,
    then: () =>
      bandNadaTicketPriceSchema.required(
        "Amount is required for bandNada tickets",
      ),
    otherwise: () => bandNadaTicketPriceSchema.notRequired(),
  }),
  bandNadaTicketPriceMax: yup.mixed().when(["ticketType", "pricingType"], {
    is: (ticketType: string, pricingType: string) =>
      ticketType === TICKET_TYPES.BN_TICKETS.value &&
      pricingType === PRICING_TYPES.SLIDING_SCALE.value,
    then: () =>
      bandNadaTicketPriceSchema.required("Maximum price is required for sliding scale"),
    otherwise: () => bandNadaTicketPriceSchema.notRequired(),
  }),
  bandNadaTicketMax: yup.mixed().when("ticketType", {
    is: TICKET_TYPES.BN_TICKETS.value,
    then: () =>
      bandNadaTicketMaxSchema.required(
        "Amount is required for bandNada tickets",
      ),
    otherwise: () => bandNadaTicketMaxSchema.notRequired(),
  }),
  ticketPriceAtDoor: yup.mixed().when("ticketType", {
    is: TICKET_TYPES.BN_TICKETS.value,
    then: () => ticketPriceAtDoorSchema.notRequired(),
    otherwise: () => ticketPriceAtDoorSchema.notRequired(),
  }),
  pricingType: yup.string().nullable(),
  feeType: yup.string().nullable().default(FEE_TYPES.POST_PURCHASE.value),
  acceptsDonation: yup.boolean().default(false),
  donationText: yup.string().nullable(),
  ticketPrice: yup
    .number()
    .transform((value) => (isNaN(value) ? null : value))
    .transform((value) => (value === null ? null : roundToTwoDecimals(value)))
    .nullable()
    .min(0, "Amount must be zero or more"),
  ticketPriceMax: yup
    .number()
    .transform((value) => (isNaN(value) ? null : value))
    .transform((value) => (value === null ? null : roundToTwoDecimals(value)))
    .nullable()
    .min(0, "Amount must be zero or more"),
  eventTicketLinkUrl: yup.string().when("ticketType", {
    is: TICKET_TYPES.EXTERNAL_TICKETS.value,
    then: (schema) =>
      schema
        .transform((value) => (!value ? null : value))
        .test("is-url", "Must be a valid URL", (value) => {
          if (!value) return false
          try {
            new URL(value)
            return true
          } catch {
            return false
          }
        }),
    otherwise: (schema) => schema.nullable(),
  }),
  maxRsvps: yup
    .number()
    .transform((value) => (isNaN(value) ? null : value))
    .nullable()
    .positive("Must be a positive number")
    .integer("Must be a whole number"),
  announcedAt: yup
    .date()
    .nullable()
    .optional()
    .test(
      "before-show-date",
      "Announce date must be before the show date",
      function (value) {
        // Only enforced on creation - on edit, a post's announced_at was set
        // (possibly long ago, via ensure_announced_at_present) before the
        // show date could have been moved around by later edits, so
        // re-validating it here would block unrelated field changes over a
        // value the user isn't even touching in this submission.
        if (this.options.context?.isEdit) return true
        if (!value) return true
        const startDate = this.parent.startDate
        if (!startDate) return true
        return new Date(value) < new Date(startDate)
      },
    ),
})

// Legacy validation schema (for backward compatibility)
export const legacyEventValidationSchema = yup.object().shape({
  title: yup.string().required("Event name is required"),
  startDate: yup.date().required("Start date is required"),
  duration: yup.number().required("Duration is required"),
  eventType: yup.string().required("Event type is required"),
  eventStatus: yup.string().required("Event status is required"),
  eventLocation: yup
    .object()
    .shape({
      displayName: yup.string().optional(),
    })
    .nullable()
    .optional(),
  eventTicketLinkUrl: yup.string().url("Must be a valid URL").nullable(),
  description: yup.string().required("Description is required"),
  coverImage: yup.mixed().nullable(),
  maxRsvps: yup.number().positive("Must be a positive number").nullable(),
})
